import { AuthData, getUserFromStorage } from "../auth";
import {
  RestFeeds,
  RestFeedPollResult,
  RestUserFeed,
  RestFolderUpdate,
  RestReadMarkerState,
  PostMarker,
  PostStarPatch,
  RestStarList,
  RestUnreadFeed,
  RestNotice,
  FeedFilter,
  MAX_SAVED_POSTS
} from "../../schema-copy/api-schema";
import {
  getReadMarkers,
  pollFeeds,
  updateFolders,
  patchStarred,
  patchReadLater,
  subscribeFeed,
  unsubscribeFeed,
  getStarList,
  getUnreadFeed,
  getFeed
} from "../api";
import { mergePostStarPatches, parseStarEntries, computeAutoReadBoundary } from "../../schema-copy/readmarker-helper";
import { postCountEngine } from "../postcount-engine";
import { getSinglePost } from "../post-engine";
import { FetchError } from "t-lib-ui/connector";
import { withFeedNameOverride, onFeedNameOverridesChange } from "../feed-name-overrides";
import { removeCustomFeedName, applyServerPreferences } from "../preferences";
import { logWithTime } from "../../utils/log";
import {
  ReadMarkerEngine,
  SerializedFeedMarker,
  UnreadChangeCallback,
  LocalFeedMarker,
  FeedLimits,
  MAX_TRACKED_POSTS
} from "./read-marker-engine";
import { FeedFilterEngine } from "./feed-filter-engine";

// Shared engine types + the public contract now live in ./feed-engine (the abstract base).
import {
  FeedEngine,
  ONBOARDING_MAX_UNREAD,
  ONBOARDING_MAX_AGE_HOURS,
  EngineState,
  OfflineMode,
  OfflineChangeCallback,
  StateChangeCallback,
  FeedsChangeCallback,
  StarChangeCallback,
  NoticeChangeCallback,
  LocalNotice,
  applyCurrentFileLocation,
} from "./feed-engine";

function isNoResponseError(e: unknown): boolean {
  return e instanceof FetchError && e.type == 'connection';
}

// Constants
// Hard cap on how long star/read-later changes may sit unsynced while the user
// keeps toggling (each toggle otherwise resets the SYNC_DELAY_MS debounce). A
// long marking session must still flush so a background poll's marker update
// can't clobber a large batch of not-yet-synced entries.
const SYNC_DELAY_MS = 3000;
const MAX_STAR_SYNC_DELAY_MS = 15000;
// After a failed star/read-later POST (e.g. offline), keep retrying every
// minute until it lands, instead of waiting for the next user toggle.
const STAR_SYNC_RETRY_MS = 60000;
const BROADCAST_CHANNEL_NAME = 'downread-feed-engine';

// BroadcastChannel message types
interface BroadcastReloadMessage {
  type: 'reload';
  authId: string;
}

interface BroadcastMarkerMessage {
  type: 'marker';
  authId: string;
  markers: SerializedFeedMarker[];
  nextPollSuggestedTime: number;
}

interface BroadcastStarMessage {
  type: 'star';
  authId: string;
  starred: string[];
  starredUpdate: string;
  readLater: string[];
  readLaterUpdate: string;
}

type BroadcastMessage = BroadcastReloadMessage | BroadcastMarkerMessage | BroadcastStarMessage;

interface UnreadFeedCache {
  entries: string[];
  feeds: Record<string, RestUserFeed>;
  serverTime: number;
  cursor?: string;
  oldestSemi?: number;
  newestSemi?: number;
  loadTime: number;
  newPostsSinceLoad: boolean;
}

export class AuthFeedEngine extends FeedEngine {
  private auth: AuthData | null = null;
  private state: EngineState = 'OFF';
  private data: RestFeeds | null = null;
  private markerEngine: ReadMarkerEngine;
  private filterEngine: FeedFilterEngine;
  private recentlySubscribed: Map<string, number> = new Map(); // feedId → timestamp
  private refreshingFeeds: Map<string, number> = new Map(); // feedId → timestamp of server-confirmed fetch start

  // Auto-mark-read limits: cached recent post times per feed. postCount is the basis
  // at snapshot time — semis[i] belongs to index (postCount-1-i),
  // so the snapshot stays valid as new (by definition fresh) posts arrive.
  private feedSemis: Map<string, { semis: number[]; postCount: number; fetchedAt: number }> = new Map();
  private feedSemisFetches: Map<string, Promise<void>> = new Map();
  private autoReadTimer: ReturnType<typeof setInterval> | null = null;
  // Debounced enforcement per feed so slider drags don't mark at intermediate values.
  private enforceDebounce: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private parsedStarred: Map<string, Set<number>> = new Map();
  private parsedReadLater: Map<string, Set<number>> = new Map();
  private starListFeeds: Map<string, RestUserFeed> = new Map();

  // Polling state
  private lastServerTime = 0;
  private nextPollSuggestedTime = Date.now() + 60000;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  // Counts consecutive no-response failures. Drives short initial retries
  // when 'online' fires before the network is actually usable (e.g. DHCP not done yet).
  private offlineRetryCount = 0;

  // Starred/readLater sync state
  private pendingStarredPatch: PostStarPatch | null = null;
  private pendingReadLaterPatch: PostStarPatch | null = null;
  private starredSyncTimer: ReturnType<typeof setTimeout> | null = null;
  private readLaterSyncTimer: ReturnType<typeof setTimeout> | null = null;
  // Timestamp of the first un-flushed change in the current debounce window
  // (used to enforce MAX_STAR_SYNC_DELAY_MS); reset on flush.
  private starredFirstQueuedAt = 0;
  private readLaterFirstQueuedAt = 0;

  // Star list cache (for fetchStarredList/fetchReadLaterList)
  private starredListCache: RestStarList | null = null;
  private starredListCacheTime = 0;
  private starredListLastUpdate: string | null = null;
  private readLaterListCache: RestStarList | null = null;
  private readLaterListCacheTime = 0;
  private readLaterListLastUpdate: string | null = null;
  // Single-flight guards so concurrent callers (e.g. several placeholder posts
  // resolving an unsubscribed feed at once) share one network request per list.
  private starListFetches: Map<string, Promise<RestStarList | null>> = new Map();

  // New Posts feed cache (one per sort order)
  // Keyed by sortOrder, or `${sortOrder}|${folder}` for folder-scoped unread lists
  private unreadFeedCaches = new Map<string, UnreadFeedCache>();
  private unreadFeedCallbacks: Set<() => void> = new Set();

  // Server notice state
  private currentNotice: LocalNotice | null = null;

  // Callbacks
  private stateCallbacks: Set<StateChangeCallback> = new Set();
  private feedsChangeCallbacks: Set<FeedsChangeCallback> = new Set();
  private starChangeCallbacks: Set<StarChangeCallback> = new Set();
  private noticeCallbacks: Set<NoticeChangeCallback> = new Set();

  // Cross-tab sync
  private broadcastChannel: BroadcastChannel | null = null;
  private needsReloadOnResume = false;

  // Prevent concurrent enable calls (e.g., React Strict Mode double-invoke)
  private enablePromise: Promise<void> | null = null;

  // Beforeunload handler to prevent data loss
  private beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

  // Offline tracking (independent of engine state)
  private offlineMode: OfflineMode = 'none';
  private offlineCallbacks: Set<OfflineChangeCallback> = new Set();
  private onlineListener: (() => void) | null = null;
  private offlineListener: (() => void) | null = null;

  private overridesUnsubscribe: (() => void) | null = null;

  constructor() {
    super();
    this.markerEngine = new ReadMarkerEngine({
      getAuth: () => this.auth,
      getFeed: (feedId) => this.data?.feeds.find(f => f.id == feedId) ?? this.starListFeeds.get(feedId),
      getFeeds: () => this.data?.feeds ?? [],
      broadcastMarkers: (_feedIds, serialized) => this.broadcastMarkers(serialized),
    });
    this.filterEngine = new FeedFilterEngine({
      getFeed: (feedId) => this.data?.feeds.find(f => f.id == feedId),
      isPostRead: (feedId, postIndex) => this.markerEngine.isPostRead(feedId, postIndex),
      getUnreadIndices: (feedId) => this.markerEngine.getUnreadIndices(feedId),
      markPostsRead: (feedId, postIndices) => this.markerEngine.markPostsRead(feedId, postIndices),
      getUserPlan: () => getUserFromStorage()?.userPlan,
    });
    this.initBroadcastChannel();
    this.overridesUnsubscribe = onFeedNameOverridesChange(() => this.notifyFeedsChange());
  }

  private initBroadcastChannel() {
    if (typeof BroadcastChannel == 'undefined') return;

    this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    this.broadcastChannel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
      const msg = event.data;
      // Cross-tab sync is for the SAME user across multiple tabs: auth.id is the
      // user id, shared across that user's tabs (persisted in localStorage), so
      // accept messages whose authId matches ours. A different authId means
      // another account is logged in in another tab — ignore it. (BroadcastChannel
      // never delivers a tab its own messages, so there's no self-echo to filter.)
      const accepted = this.state != 'OFF' && this.state != 'ERROR' && !!this.auth && !!this.data && msg.authId == this.auth?.id;
      console.log(`[broadcast] ← received '${msg.type}' (${accepted ? 'handling' : 'ignored'}; state=${this.state}, ownAuth=${this.auth?.id ?? 'none'}, msgAuth=${msg.authId})`, msg);
      if (accepted)
        this.handleBroadcastMessage(msg);
    };
  }

  private handleBroadcastMessage(msg: BroadcastMessage) {
    if (msg.type == 'reload') {
      this.handleReloadMessage();
    } else if (msg.type == 'marker') {
      this.handleMarkerMessage(msg);
    } else if (msg.type == 'star') {
      this.handleStarMessage(msg);
    }
  }

  private handleReloadMessage() {
    if (this.state == 'PAUSED') {
      // Mark for reload on next resume
      this.needsReloadOnResume = true;
    } else if (this.state == 'READY' || this.state == 'INTERRUPTED') {
      // Reload immediately
      this.reloadData();
    }
  }

  private handleMarkerMessage(msg: BroadcastMarkerMessage) {
    if (!this.data) return;

    this.markerEngine.handleBroadcastMessage(msg.markers);
    // A broadcast can change a feed's bitmap while leaving its unread count
    // unchanged (e.g. correcting a drifted read set), and the marker engine only
    // emits unread notifications on count changes. Fire the feeds-change hook
    // unconditionally — exactly like applyMarkerUpdate does for poll pushes — so
    // Main rebuilds the visible post list from the corrected bitmap. Otherwise a
    // feed can stay stuck on "All caught up" while the engine reports unread.
    this.notifyFeedsChange();

    // Update nextPollSuggestedTime with random offset to avoid all tabs polling at once
    const randomOffset = 15000 + Math.random() * 45000; // 15-60 seconds
    this.nextPollSuggestedTime = msg.nextPollSuggestedTime + randomOffset;
    if (this.state == 'READY' || this.state == 'INTERRUPTED') {
      this.schedulePoll();
    }
  }

  async reloadData(): Promise<void> {
    if (!this.auth) return;

    this.clearUnreadFeedCache();
    // Use UPDATING if we have data, LOADING if we don't
    this.setState(this.data ? 'UPDATING' : 'LOADING');
    try {
      const data = await getReadMarkers(this.auth);
      if (!data) {
        this.setState('ERROR');
        return;
      }

      this.data = data;
      this.applyNotice(data.notice);
      applyServerPreferences(data.preferences);
      this.updatePostCountCache(data.feeds);
      this.markerEngine.loadFromData(data.feedMarkers, data.bitmaps, data.feeds, data.serverTime);
      this.seedFeedSemisAndEnforce(data.feeds);
      this.filterEngine.enable(this.auth.id);
      this.filterEngine.seedFilters(data.preferences?.feedFilters);
      this.parseStarData();
      this.lastServerTime = data.serverTime;
      this.markOnlineSuccess();
      this.setState('READY');
      this.notifyFeedsChange();
      this.schedulePoll();
    } catch (e) {
      console.error('Failed to reload read markers:', e);
      // Network error: treat as transient, keep retrying instead of locking the engine in
      // ERROR. Common when 'online' fires before the network is actually usable.
      if (isNoResponseError(e)) {
        if (this.data) {
          this.setOfflineMode('poll');
          this.offlineRetryCount++;
          this.nextPollSuggestedTime = Date.now() + this.getOfflineRetryDelay();
          this.setState('INTERRUPTED');
          this.schedulePoll();
        } else {
          // Initial load still hasn't succeeded — keep retrying (state stays LOADING).
          this.scheduleInitialLoadRetry();
        }
        return;
      }
      this.setState('ERROR');
    }
  }

  // Retry a failed initial load (no data yet, so the INTERRUPTED/poll machinery can't run).
  // The engine stays in LOADING; the timer shares pollTimer so disable()'s stopPoll cancels it.
  private scheduleInitialLoadRetry() {
    this.setOfflineMode('poll');
    this.offlineRetryCount++;
    this.stopPoll();
    this.pollTimer = setTimeout(() => this.reloadData(), this.getOfflineRetryDelay());
  }

  // Progressive backoff for retries while offline (offlineMode == 'poll').
  // Short initial delays let us recover quickly from transient conditions
  // like a freshly-fired 'online' event before DHCP completes.
  private getOfflineRetryDelay(): number {
    const delays = [2000, 4000, 8000, 15000, 30000, 60000];
    const idx = Math.min(Math.max(this.offlineRetryCount - 1, 0), delays.length - 1);
    return delays[idx];
  }

  private markOnlineSuccess() {
    if (this.offlineMode != 'none') this.setOfflineMode('none');
    this.offlineRetryCount = 0;
  }

  private broadcastReload() {
    if (!this.broadcastChannel || !this.auth) return;

    const msg: BroadcastReloadMessage = {
      type: 'reload',
      authId: this.auth.id
    };
    console.log(`[broadcast] → sending 'reload' (authId=${msg.authId})`, msg);
    this.broadcastChannel.postMessage(msg);
  }

  private broadcastMarkers(serialized: SerializedFeedMarker[]) {
    if (!this.broadcastChannel || !this.auth || !serialized.length) return;

    const msg: BroadcastMarkerMessage = {
      type: 'marker',
      authId: this.auth.id,
      markers: serialized,
      nextPollSuggestedTime: this.nextPollSuggestedTime
    };
    console.log(`[broadcast] → sending 'marker' (${serialized.length} feed(s): ${serialized.map(m => m.feedId).join(', ')})`, msg);
    this.broadcastChannel.postMessage(msg);
  }

  // --- Callback Registration ---

  onStateChange(callback: StateChangeCallback): () => void {
    this.stateCallbacks.add(callback);
    return () => this.stateCallbacks.delete(callback);
  }

  onUnreadChange(callback: UnreadChangeCallback): () => void {
    return this.markerEngine.onUnreadChange(callback);
  }

  onFeedsChange(callback: FeedsChangeCallback): () => void {
    this.feedsChangeCallbacks.add(callback);
    return () => this.feedsChangeCallbacks.delete(callback);
  }

  onStarChange(callback: StarChangeCallback): () => void {
    this.starChangeCallbacks.add(callback);
    return () => this.starChangeCallbacks.delete(callback);
  }

  onNoticeChange(callback: NoticeChangeCallback): () => void {
    this.noticeCallbacks.add(callback);
    return () => this.noticeCallbacks.delete(callback);
  }

  // --- Offline State ---

  onOfflineChange(callback: OfflineChangeCallback): () => void {
    this.offlineCallbacks.add(callback);
    return () => this.offlineCallbacks.delete(callback);
  }

  getOfflineMode(): OfflineMode {
    return this.offlineMode;
  }

  isOffline(): boolean {
    return this.offlineMode != 'none';
  }

  private setOfflineMode(mode: OfflineMode) {
    if (this.offlineMode == mode) return;
    this.offlineMode = mode;
    this.offlineCallbacks.forEach(cb => {
      try { cb(mode); } catch (e) { console.error('Offline callback error:', e); }
    });
  }

  private installOnlineListeners() {
    if (typeof window == 'undefined' || this.onlineListener) return;
    this.onlineListener = () => this.handleBrowserOnline();
    this.offlineListener = () => this.handleBrowserOffline();
    window.addEventListener('online', this.onlineListener);
    window.addEventListener('offline', this.offlineListener);
    // Initialize from current browser state
    if (typeof navigator != 'undefined' && navigator.onLine === false)
      this.handleBrowserOffline();
  }

  private removeOnlineListeners() {
    if (typeof window == 'undefined') return;
    if (this.onlineListener) {
      window.removeEventListener('online', this.onlineListener);
      this.onlineListener = null;
    }
    if (this.offlineListener) {
      window.removeEventListener('offline', this.offlineListener);
      this.offlineListener = null;
    }
  }

  private handleBrowserOffline() {
    this.setOfflineMode('event');
    // Pause the engine if it was running
    if (this.state == 'READY' || this.state == 'UPDATING' || this.state == 'INTERRUPTED') {
      this.stopPoll();
      this.setState('PAUSED');
    }
  }

  private handleBrowserOnline() {
    // Clear offline state; resume if paused due to offline event.
    // Reset retry count so reloadData/poll attempts on resume use the short
    // initial delay if the network isn't actually usable yet (DHCP, captive portal).
    const wasEventOffline = this.offlineMode == 'event';
    this.setOfflineMode('none');
    this.offlineRetryCount = 0;
    if (!this.auth) return;
    if (this.state == 'ERROR')
      this.reloadData();
    else if (wasEventOffline && this.state == 'PAUSED')
      this.resume();
  }

  getNotice(): LocalNotice | null {
    return this.currentNotice;
  }

  private applyNotice(notice: RestNotice | undefined) {
    if (!notice) {
      if (this.currentNotice) {
        this.currentNotice = null;
        this.noticeCallbacks.forEach(cb => cb(null));
      }
      return;
    }
    const local: LocalNotice = {
      message: notice.message,
      shutdownAtMs: notice.secondsTillShutdown != null ? Date.now() + notice.secondsTillShutdown * 1000 : undefined,
    };
    this.currentNotice = local;
    this.noticeCallbacks.forEach(cb => cb(local));
  }

  // --- State Management ---

  getState(): EngineState {
    return this.state;
  }

  getData(): RestFeeds | null {
    return this.data;
  }

  getDiscoverDate(): string | undefined {
    return this.data?.discoverDate || undefined;
  }

  async waitForDiscoverDate(): Promise<string> {
    // If we already have data, return it immediately
    if (this.data?.discoverDate) {
      return this.data.discoverDate;
    }

    // Wait for the engine to reach a state where data is available
    return new Promise<string>((resolve, reject) => {
      const unregisterCallback = this.onStateChange((newState) => {
        if (newState == 'READY' || newState == 'PAUSED' || newState == 'UPDATING' || newState == 'INTERRUPTED') {
          unregisterCallback();
          resolve(this.data?.discoverDate ?? '');
        } else if (newState == 'ERROR') {
          unregisterCallback();
          reject(new Error('Cannot get discover date: engine failed to load'));
        }
      });
    });
  }

  // --- Read marker delegations ---

  getSortOrder(feedId: string): 'N' | 'O' | 'A' | '?' {
    return this.markerEngine.getSortOrder(feedId);
  }

  setSortOrder(feedId: string, order: 'N' | 'O' | 'A'): void {
    this.markerEngine.setSortOrder(feedId, order);
  }

  getShowRead(feedId: string): 'u' | 'a' {
    return this.markerEngine.getShowRead(feedId);
  }

  setShowRead(feedId: string, showRead: 'u' | 'a'): void {
    this.markerEngine.setShowRead(feedId, showRead);
  }

  getCompactMode(feedId: string): 'c' | 'f' | 'l' {
    return this.markerEngine.getCompactMode(feedId);
  }

  setCompactMode(feedId: string, mode: 'c' | 'f' | 'l'): void {
    this.markerEngine.setCompactMode(feedId, mode);
  }

  // --- Auto-mark-read limits ---

  getFeedLimits(feedId: string): FeedLimits {
    return this.markerEngine.getFeedLimits(feedId);
  }

  /** Sets the auto-read limits (0 = off) and enforces them after a short debounce. */
  setFeedLimits(feedId: string, maxUnread: number, maxAgeDays: number): void {
    this.markerEngine.setFeedLimits(feedId, maxUnread, maxAgeDays);
    const pending = this.enforceDebounce.get(feedId);
    if (pending) clearTimeout(pending);
    this.enforceDebounce.set(feedId, setTimeout(() => {
      this.enforceDebounce.delete(feedId);
      this.ensureFeedSemis(feedId).then(() => this.enforceAutoRead(feedId));
    }, 1500));
  }

  getFeedFilter(feedId: string): FeedFilter | undefined {
    return this.filterEngine.getFilter(feedId);
  }

  /** Applies a filter edit engine-side (incl. the retroactive pass over recent posts).
   *  The caller persists the filter into UserPreferences.feedFilters. */
  setFeedFilter(feedId: string, filter: FeedFilter | undefined): Promise<void> {
    return this.filterEngine.setFilter(feedId, filter);
  }

  previewFeedFilter(feedId: string, filter: FeedFilter | undefined): Promise<number> {
    return this.filterEngine.previewFilter(feedId, filter);
  }

  /** Fetches and caches the feed's recent post times (15 min TTL). Used by the limits UI preview. */
  async ensureFeedSemis(feedId: string): Promise<void> {
    const cached = this.feedSemis.get(feedId);
    if (cached && Date.now() - cached.fetchedAt < 15 * 60_000) return;
    const inFlight = this.feedSemisFetches.get(feedId);
    if (inFlight) return inFlight;
    if (!this.auth) return;
    const request = getFeed(this.auth, feedId, true)
      .then(feed => {
        this.feedSemis.set(feedId, {
          semis: feed.recentPostsSemi ?? [],
          postCount: feed.postCount,
          fetchedAt: Date.now()
        });
      })
      .catch(e => console.error('Failed to fetch post times:', e))
      .finally(() => this.feedSemisFetches.delete(feedId));
    this.feedSemisFetches.set(feedId, request);
    return request;
  }

  /** How many posts the given limits would mark read right now; undefined while post times load. */
  previewAutoRead(feedId: string, maxUnread: number, maxAgeDays: number): number | undefined {
    const feed = this.getFeed(feedId);
    const basis = feed?.postCount ?? 0;
    if (!basis) return 0;
    const snap = this.feedSemis.get(feedId);
    if (maxAgeDays && !snap) return undefined;
    const boundary = computeAutoReadBoundary(basis, snap?.semis, snap?.postCount ?? basis,
      maxUnread, maxAgeDays, Date.now());
    if (boundary < 0) return 0;
    return this.markerEngine.countUnreadAtOrBelow(feedId, boundary);
  }

  // Marks posts read per the feed's limits. No-ops (queues nothing) when nothing
  // unread lies below the boundary.
  private enforceAutoRead(feedId: string): void {
    const { maxUnread, maxAgeDays } = this.markerEngine.getFeedLimits(feedId);
    if (!maxUnread && !maxAgeDays) return;
    const feed = this.data?.feeds.find(f => f.id == feedId);
    const basis = feed?.postCount ?? 0;
    if (!basis) return;
    const snap = this.feedSemis.get(feedId);
    const boundary = computeAutoReadBoundary(basis, snap?.semis, snap?.postCount ?? basis,
      maxUnread, maxAgeDays, Date.now());
    if (boundary < 0) return;
    if (!this.markerEngine.countUnreadAtOrBelow(feedId, boundary)) return;
    // Clamp to the trackable window: replay expands ranges index-by-index, and
    // posts below it can't be tracked read anyway.
    const start = Math.max(0, basis - MAX_TRACKED_POSTS);
    console.log(`[auto-read] feed ${feedId}: marking posts ${start}-${boundary} read (maxUnread=${maxUnread ?? '-'}, maxAgeDays=${maxAgeDays ?? '-'})`);
    this.markerEngine.markPostsRead(feedId, [[start, boundary]]);
  }

  private enforceAutoReadAll(): void {
    this.markerEngine.getAllFeedMarkers().forEach((marker, feedId) => {
      if (marker.maxUnread || marker.maxAgeDays) this.enforceAutoRead(feedId);
    });
  }

  // Seed the semis cache from a feeds load (the server attaches recentPostsSemi
  // to feeds with limits configured) and apply the limits.
  private seedFeedSemisAndEnforce(feeds: RestUserFeed[]): void {
    const now = Date.now();
    for (const feed of feeds)
      if (feed.recentPostsSemi)
        this.feedSemis.set(feed.id, {
          semis: feed.recentPostsSemi,
          postCount: feed.postCount,
          fetchedAt: now
        });
    this.enforceAutoReadAll();
    if (!this.autoReadTimer)
      this.autoReadTimer = setInterval(() => this.enforceAutoReadAll(), 15 * 60_000);
  }

  getFeedMarker(feedId: string): LocalFeedMarker | undefined {
    return this.markerEngine.getFeedMarker(feedId);
  }

  getAllFeedMarkers(): Map<string, LocalFeedMarker> {
    return this.markerEngine.getAllFeedMarkers();
  }

  isPostRead(feedId: string, postIndex: number): boolean {
    return this.markerEngine.isPostRead(feedId, postIndex);
  }

  getReadForFeed(feedId: string): Set<number> {
    return this.markerEngine.getReadForFeed(feedId);
  }

  getUnreadCount(feedId: string): number {
    return this.markerEngine.getUnreadCount(feedId);
  }

  markPostRead(feedId: string, postIndex: number): void {
    this.markerEngine.markPostRead(feedId, postIndex);
  }

  markPostUnread(feedId: string, postIndex: number): void {
    this.markerEngine.markPostUnread(feedId, postIndex);
  }

  markPostsRead(feedId: string, postIndices: PostMarker[]): void {
    this.markerEngine.markPostsRead(feedId, postIndices);
  }

  markAllRead(feedId: string): void {
    this.markerEngine.markAllRead(feedId);
  }

  markAllUnread(feedId: string): void {
    this.markerEngine.markAllUnread(feedId);
  }

  preloadFeed(feedId: string, count = 10): void {
    this.markerEngine.preloadFeed(feedId, count);
  }

  /** Mark every post older than `days` days as read (keeping nothing extra). */
  markOlderThanDaysRead(feedId: string, days: number): Promise<void> {
    return this.markOlderThanKeepRead(feedId, days, 0);
  }

  /**
   * Mark posts older than `days` days as read, but always keep the newest `keep`
   * posts unread. Posts are stored chronologically (index 0 = oldest), so we
   * binary-search the CDN archives for the age boundary — bounded to ~log2(postCount)
   * post fetches — then cap it so the newest `keep` posts are never marked.
   * With days = 0 this acts as a pure "keep only the newest `keep` posts" operation.
   */
  async markOlderThanKeepRead(feedId: string, days: number, keep: number): Promise<void> {
    const feed = this.getFeed(feedId);
    if (!feed) return;
    const postCount = feed.postCount;
    if (!postCount) return;

    const keepBoundary = postCount - keep - 1; // highest index allowed to be marked
    if (keepBoundary < 0) return;

    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    let lo = 0, hi = postCount - 1, olderBoundary = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const post = await getSinglePost(feed, mid).catch(() => undefined);
      const t = post?.pubDate ? new Date(post.pubDate).getTime() : 0;
      if (t < cutoff) { olderBoundary = mid; lo = mid + 1; }
      else hi = mid - 1;
    }

    const boundary = Math.min(olderBoundary, keepBoundary);
    if (boundary >= 0) this.markerEngine.markPostsRead(feedId, [[0, boundary]]);
  }

  // --- Feeds / folders ---

  getFeeds(): RestUserFeed[] {
    return (this.data?.feeds ?? []).map(withFeedNameOverride);
  }

  getFeed(feedId: string): RestUserFeed | undefined {
    const raw = this.data?.feeds.find(f => f.id == feedId) ?? this.starListFeeds.get(feedId);
    return raw && withFeedNameOverride(raw);
  }

  // Like getFeed but skips the user's custom-name override. Used by the rename UI
  // so users can see the original name when editing or resetting.
  getRawFeed(feedId: string): RestUserFeed | undefined {
    return this.data?.feeds.find(f => f.id == feedId) ?? this.starListFeeds.get(feedId);
  }

  registerFeedInfo(feed: RestUserFeed): void {
    this.starListFeeds.set(feed.id, feed);
  }

  // Resolve metadata for a feed that may not be subscribed: such feeds are only
  // known via the star lists (starred / read-later), so when getFeed misses,
  // fetch those lists to populate starListFeeds and retry. Used by lazy post
  // loading in saved lists, where posts from unsubscribed feeds would otherwise
  // stay empty placeholders until the list metadata happens to arrive.
  async ensureFeedInfo(feedId: string): Promise<RestUserFeed | undefined> {
    let feed = this.getFeed(feedId);
    if (feed) return feed;
    await this.fetchStarredList();
    feed = this.getFeed(feedId);
    if (feed) return feed;
    await this.fetchReadLaterList();
    return this.getFeed(feedId);
  }

  getFolders(): RestFolderUpdate {
    return this.data?.folders ?? {};
  }

  private setState(newState: EngineState) {
    if (this.state == newState) return;
    const prevState = this.state;
    this.state = newState;
    this.stateCallbacks.forEach(cb => {
      try { cb(newState, prevState); } catch (e) { console.error('State callback error:', e); }
    });
  }

  private notifyFeedsChange() {
    if (!this.data) return;
    const feeds = this.data.feeds.map(withFeedNameOverride);
    this.feedsChangeCallbacks.forEach(cb => {
      try { cb(feeds, this.data!.folders); } catch (e) { console.error('Feeds change callback error:', e); }
    });
  }

  // --- Engine Control ---

  async enable(auth?: AuthData): Promise<void> {
    // The auth engine is never enabled without auth (the abstract base widens the param so the
    // anon engine, which ignores it, stays signature-compatible).
    if (!auth) return;

    // If already enabling, wait for it to complete
    if (this.enablePromise) {
      await this.enablePromise;
      return;
    }

    // If already enabled with same auth, ignore
    if (this.auth?.id == auth.id && this.state != 'OFF' && this.state != 'ERROR') {
      return;
    }


    // Only disable if switching to different auth
    if (this.auth && this.auth.id != auth.id) {
      this.disable();
    } else if (this.state == 'OFF' || this.state == 'ERROR') {
      // Clean slate for initial enable or after error
      this.disable();
    } else {
      // Already loading/loaded with same auth
      return;
    }

    this.auth = auth;
    this.setState('LOADING');
    // Install BEFORE the initial load: if it fails (mobile radio waking up, page suspended
    // mid-boot), the 'online' event must be able to trigger the recovery reload below.
    this.installOnlineListeners();

    // Store promise to prevent concurrent calls
    this.enablePromise = (async () => {
      try {

        const data = await getReadMarkers(auth);



        // Check if we were disabled while loading (e.g., React Strict Mode unmount)
        if (this.auth?.id != auth.id) {
          return;
        }

        if (!data) {
          this.setState('ERROR');
          return;
        }

        this.data = data;
        this.applyNotice(data.notice);
        this.updatePostCountCache(data.feeds);
        this.markerEngine.loadFromData(data.feedMarkers, data.bitmaps, data.feeds, data.serverTime);
        this.seedFeedSemisAndEnforce(data.feeds);
        this.filterEngine.enable(auth.id);
        this.filterEngine.seedFilters(data.preferences?.feedFilters);
        this.parseStarData();
        this.lastServerTime = data.serverTime;
        this.setState('READY');
        this.notifyFeedsChange();
        this.schedulePoll();
        this.installBeforeUnloadHandler();
        this.checkSubscribeSignup();
      } catch (e) {
        console.error('Failed to load read markers:', e);
        // A transient network failure on the very first load (radio still waking up, the fetch
        // timeout, the page suspended mid-boot — common on mobile): retry with backoff instead
        // of parking in ERROR, which nothing recovers from without a full page reload.
        if (this.auth?.id == auth.id && isNoResponseError(e)) {
          this.scheduleInitialLoadRetry();
          return;
        }
        this.setState('ERROR');
      } finally {
        this.enablePromise = null;
      }
    })();

    return this.enablePromise;
  }

  disable(): void {
    this.removeBeforeUnloadHandler();
    this.removeOnlineListeners();
    this.setOfflineMode('none');
    this.stopPoll();
    this.markerEngine.flush();
    this.flushStarredSync();
    this.flushReadLaterSync();
    this.auth = null;
    this.data = null;
    this.currentNotice = null;
    this.markerEngine.clear();
    this.filterEngine.disable();
    this.feedSemis.clear();
    this.feedSemisFetches.clear();
    if (this.autoReadTimer) {
      clearInterval(this.autoReadTimer);
      this.autoReadTimer = null;
    }
    this.enforceDebounce.forEach(timer => clearTimeout(timer));
    this.enforceDebounce.clear();
    this.parsedStarred.clear();
    this.parsedReadLater.clear();
    this.starListFeeds.clear();
    this.starredListCache = null;
    this.starredListCacheTime = 0;
    this.starredListLastUpdate = null;
    this.readLaterListCache = null;
    this.readLaterListCacheTime = 0;
    this.readLaterListLastUpdate = null;
    this.starListFetches.clear();
    this.clearUnreadFeedCache();
    this.lastServerTime = 0;
    this.setState('OFF');
  }

  destroy(): void {
    this.removeBeforeUnloadHandler();
    this.disable();
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
    this.stateCallbacks.clear();
    this.feedsChangeCallbacks.clear();
    this.starChangeCallbacks.clear();
    this.noticeCallbacks.clear();
    this.offlineCallbacks.clear();
    this.overridesUnsubscribe?.();
    this.overridesUnsubscribe = null;
  }

  private installBeforeUnloadHandler() {
    if (typeof window == 'undefined' || this.beforeUnloadHandler) return;

    this.beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      const hasPendingChanges =
        this.markerEngine.hasPendingChanges() ||
        this.pendingStarredPatch != null ||
        this.pendingReadLaterPatch != null;

      if (hasPendingChanges) {
        // Try to flush changes in the background
        this.markerEngine.flush();
        this.flushStarredSync();
        this.flushReadLaterSync();

        // Show confirmation dialog
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private removeBeforeUnloadHandler() {
    if (typeof window == 'undefined' || !this.beforeUnloadHandler) return;

    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.beforeUnloadHandler = null;
  }

  pause(): void {
    if (this.state == 'READY' || this.state == 'UPDATING' || this.state == 'INTERRUPTED') {
      this.stopPoll();
      this.setState('PAUSED');
    }
  }

  async resume(): Promise<void> {
    if (!this.auth) return;
    // Coming back to the foreground is the recovery point for a failed load: without this,
    // ERROR is terminal and the user sees an empty library until a full page reload.
    if (this.state == 'ERROR') return this.reloadData();
    if (this.state != 'PAUSED') return;

    // Check if data is very stale (older than 5 minutes) or another tab requested reload
    const now = Date.now();
    const isStale = this.nextPollSuggestedTime < (now - 5 * 60 * 1000);

    if (isStale || this.needsReloadOnResume) {
      this.needsReloadOnResume = false;
      await this.reloadData();
    } else {
      // Always poll immediately on resume (e.g. after wake from sleep)
      // so that feed postCounts are up-to-date for the unread feed
      this.setState('READY');
      await this.poll();
    }
  }

  // --- Polling ---

  private schedulePoll() {
    this.stopPoll();
    if (this.state != 'READY' && this.state != 'INTERRUPTED') return;

    const now = Date.now();
    let delay = Math.max(0, this.nextPollSuggestedTime - now);

    // Accelerate polling when there are recently subscribed feeds waiting for posts
    if (this.recentlySubscribed.size && delay > 10_000)
      delay = 10_000;

    this.pollTimer = setTimeout(() => { this.poll();}, delay);
  }

  private stopPoll() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  async pollNow(): Promise<void> {
    // Only run if idle; if UPDATING/PAUSED/LOADING/OFF/ERROR, the pending or
    // scheduled work already covers this. schedulePoll is called from poll()
    // on completion, so the regular cadence stays intact.
    if (this.state == 'READY') await this.poll();
  }

  private async poll(): Promise<void> {
    if (!this.auth || !this.data) return;
    if (this.state != 'READY' && this.state != 'INTERRUPTED') return;

    // Prune expired recently-subscribed entries
    const now = Date.now();
    this.recentlySubscribed.forEach((ts, id) => {
      if (now - ts > 60_000) this.recentlySubscribed.delete(id);
    });

    this.setState('UPDATING');

    try {
      logWithTime('Polling server for feed updates');
      const result = await pollFeeds(this.auth, this.lastServerTime);
      this.applyNotice(result.notice);
      this.applyPollResult(result);
      if (result.markers)
        this.applyMarkerUpdate(result.markers, result.serverTime);
      this.lastServerTime = result.serverTime;
      this.nextPollSuggestedTime = Date.now() + (result.nextPollSuggestedTime - result.serverTime);
      this.markOnlineSuccess();
      this.setState('READY');
      this.schedulePoll();
    } catch (e) {
      console.error('Poll failed:', e);
      if (isNoResponseError(e)) {
        this.setOfflineMode('poll');
        this.offlineRetryCount++;
      }
      this.setState('INTERRUPTED');
      // Retry with progressive backoff when offline so we recover quickly
      // (e.g. when 'online' fires before DHCP is done).
      this.nextPollSuggestedTime = Date.now() + (this.offlineMode == 'poll' ? this.getOfflineRetryDelay() : 60000);
      this.schedulePoll();
    }
  }

  private applyPollResult(result: RestFeedPollResult) {
    if (!this.data) return;

    const modifiedFeedIds: string[] = [];

    for (const [feedId, update] of Object.entries(result.updates)) {
      const feed = this.data.feeds.find(f => f.id == feedId);
      if (!feed) continue;

      // Capture the old postCount BEFORE writing the new fields so the marker engine can do
      // bitmap remap in the same coordinate space it parsed in.
      const oldPostCount = feed.postCount;
      feed.postCount = update.postCount;
      feed.lastNewPostAtSemi = update.lastNewPostAtSemi;
      feed.feedStatus = update.feedStatus;
      applyCurrentFileLocation(feed, update);
      const newPostCount = feed.postCount;

      if (this.markerEngine.handlePollPostCountUpdate(feedId, oldPostCount, newPostCount))
        modifiedFeedIds.push(feedId);
    }

    // Update postcount cache with updated feeds
    if (Object.keys(result.updates).length > 0) {
      this.updatePostCountCache(this.data.feeds);
      // New posts may push older ones over a feed's auto-read limits
      for (const feedId of Object.keys(result.updates)) {
        this.enforceAutoRead(feedId);
        this.filterEngine.onFeedUpdated(feedId);
      }
    }

    // Broadcast modified markers to other tabs and notify UI
    if (modifiedFeedIds.length) {
      this.broadcastMarkersForFeeds(modifiedFeedIds);
      this.notifyFeedsChange();
      // Mark unread feed caches as potentially stale
      this.unreadFeedCaches.forEach(cache => cache.newPostsSinceLoad = true);
    }
  }

  private broadcastMarkersForFeeds(feedIds: string[]) {
    if (!this.broadcastChannel || !this.auth) return;
    const serialized = this.markerEngine.serializeMarkers(feedIds);
    this.broadcastMarkers(serialized);
  }

  private updatePostCountCache(feeds: RestUserFeed[]) {
    const postCounts: Record<string, number> = {};
    for (const feed of feeds) {
      postCounts[feed.id] = feed.postCount;
    }
    postCountEngine.updatePostCounts(postCounts);
  }

  private applyMarkerUpdate(markers: RestReadMarkerState, serverTime: number) {
    if (!this.data) return;

    this.data.feedMarkers = markers.feedMarkers;
    this.data.bitmaps = markers.bitmaps;
    this.data.folders = markers.folders;
    // Preserve star/read-later changes the user just made that haven't been
    // POSTed yet — the server copy in `markers` doesn't have them.
    const nowIso = new Date().toISOString();
    this.data.starred = this.reconcileStarEntries(markers.starred, this.pendingStarredPatch);
    this.data.starredUpdate = this.pendingStarredPatch ? nowIso : markers.starredUpdate;
    this.data.readLater = this.reconcileStarEntries(markers.readLater, this.pendingReadLaterPatch);
    this.data.readLaterUpdate = this.pendingReadLaterPatch ? nowIso : markers.readLaterUpdate;

    // Marker engine captures oldUnreadCounts internally, then re-parses, then
    // notifies + broadcasts on its own. We just orchestrate the surrounding
    // star/folder data and downstream UI hooks.
    this.markerEngine.applyServerMarkerState(markers.feedMarkers, markers.bitmaps, this.data.feeds, serverTime);

    this.parseStarData();

    // Clear unread feed caches — can't reliably patch from server-pushed changes
    this.clearUnreadFeedCache();

    // Limits may have been set/changed on another device; the no-op guard in
    // enforceAutoRead prevents duplicate patches when it already marked there.
    this.enforceAutoReadAll();

    this.notifyFeedsChange();
  }

  private async checkSubscribeSignup() {
    if (typeof localStorage == 'undefined') return;
    const feedId = localStorage.getItem('subscribeSignup');
    if (!feedId) return;
    localStorage.removeItem('subscribeSignup');
    if (!this.data?.feeds.find(f => f.id == feedId))
      await this.subscribe(feedId);
  }

  // --- Subscriptions ---

  async subscribe(feedId: string): Promise<RestUserFeed | null> {
    if (!this.auth) {
      console.error('Subscribe failed: no auth data available');
      return null;
    }
    if (!this.data) {
      console.error('Subscribe failed: feed engine data not loaded (current state:', this.state + ')');
      return null;
    }

    // Already subscribed — return existing feed without hitting the API
    const existing = this.data.feeds.find(f => f.id == feedId);
    if (existing) return existing;

    try {
      const result = await subscribeFeed(this.auth, feedId);
      const feed = result.feed;

      // Add to local data
      if (!this.data.feeds.find(f => f.id == feedId)) {
        this.data.feeds.push(feed);
      }

      // Use server-provided marker (calculated using feed posting frequency)
      this.markerEngine.handleSubscribeResult(feed, result.feedMarker, result.bitmap);

      this.markRecentlySubscribed(feedId);
      if (result.refreshing) {
        this.markRefreshing(feedId);
        if (!feed.postCount)
          this.pollForNewFeedPosts(feedId);
      }

      // Notify other tabs to reload
      this.broadcastReload();

      // Notify about feeds/folders change
      this.notifyFeedsChange();

      return feed;
    } catch (e) {
      const type = e && typeof e == 'object' && 'type' in e ? (e as any).type : undefined;
      // The server already has this subscription (e.g. an earlier attempt whose response we lost),
      // so there is nothing left to do but pick it up locally.
      if (type == 'AlreadySubscribed')
        return await this.adoptExistingSubscription(feedId) ?? null;
      // Re-throw quota errors so callers can show appropriate UI
      if (type == 'Quota')
        throw e;
      console.error('Subscribe failed:', e);
      return null;
    }
  }

  /**
   * The server reports the user is already subscribed to a feed our local data doesn't list.
   * Reload from the server so both the subscription and its existing read markers are picked up —
   * never re-initialize markers here, that would discard the read state the subscription already has.
   */
  async adoptExistingSubscription(feedId: string): Promise<RestUserFeed | undefined> {
    if (!this.data?.feeds.find(f => f.id == feedId))
      await this.reloadData();
    const feed = this.data?.feeds.find(f => f.id == feedId);
    if (!feed) return undefined;
    this.markRecentlySubscribed(feedId);
    this.notifyFeedsChange();
    return feed;
  }

  // The server picks an initial marker from posting frequency, but the onboarding flows want the
  // same "≥10, ≤100, last 72h" cap the anon engine applies. Fetch the feed's recent post times, then
  // mark the older ones read to match.
  async applyStarterReadLimit(feedId: string): Promise<void> {
    const feed = this.data?.feeds.find(f => f.id == feedId);
    if (!feed) return;
    await this.ensureFeedSemis(feedId);
    const snap = this.feedSemis.get(feedId);
    this.applyStarterLimitWith(feedId, snap?.semis ?? feed.recentPostsSemi, snap?.postCount ?? feed.postCount, ONBOARDING_MAX_UNREAD, ONBOARDING_MAX_AGE_HOURS);
  }

  /** Count how many PRIVATE feeds the user is subscribed to */
  getPrivateFeedCount(): number {
    return this.data?.feeds.filter(f => f.feedStatus == 'PRIVATE').length ?? 0;
  }

  /** Count how many NEWSLETTER (inbox) feeds the user is subscribed to */
  getNewsletterFeedCount(): number {
    return this.data?.feeds.filter(f => f.feedStatus == 'NEWSLETTER').length ?? 0;
  }

  /** Check if a feed was recently subscribed (within last 60s) — used to show it in sidebar even with 0 unread */
  isRecentlySubscribed(feedId: string): boolean {
    const ts = this.recentlySubscribed.get(feedId);
    if (!ts) return false;
    if (Date.now() - ts > 60_000) {
      this.recentlySubscribed.delete(feedId);
      return false;
    }
    return true;
  }

  private markRecentlySubscribed(feedId: string) {
    this.recentlySubscribed.set(feedId, Date.now());
    // Schedule explicit cleanup + UI notify so the LeftSidebar's "loading" spinner disappears
    // even when nothing else triggers a re-render in the next minute (e.g. bulk OPML import
    // of feeds that already had postCount > 0, so pollForNewFeedPosts doesn't run).
    setTimeout(() => {
      const ts = this.recentlySubscribed.get(feedId);
      if (ts != undefined && Date.now() - ts >= 60_000) {
        this.recentlySubscribed.delete(feedId);
        this.notifyFeedsChange();
      }
    }, 60_500);
  }

  /** True while the sync server is fetching this feed after a subscribe (server-confirmed via
   *  RestSubscribeResult.refreshing, capped at 60s) — drives the "refreshing" spinner. */
  isRefreshing(feedId: string): boolean {
    const ts = this.refreshingFeeds.get(feedId);
    if (!ts) return false;
    if (Date.now() - ts > 60_000) {
      this.refreshingFeeds.delete(feedId);
      return false;
    }
    return true;
  }

  private markRefreshing(feedId: string) {
    this.refreshingFeeds.set(feedId, Date.now());
    this.notifyFeedsChange();
    // A refresh that adds posts clears the flag via pollForNewFeedPosts; one that finds nothing new
    // produces no other re-render, so time the spinner out explicitly.
    setTimeout(() => {
      const ts = this.refreshingFeeds.get(feedId);
      if (ts != undefined && Date.now() - ts >= 60_000) {
        this.refreshingFeeds.delete(feedId);
        this.notifyFeedsChange();
      }
    }, 60_500);
  }

  /** Integrate a subscribe result from an external call (e.g. subscribeWithMode) into the feed engine */
  handleSubscribeResult(result: { feed: RestUserFeed; feedMarker: string; bitmap: string; refreshing?: boolean }) {
    if (!this.data) return;
    const { feed } = result;
    if (!this.data.feeds.find(f => f.id == feed.id))
      this.data.feeds.push(feed);
    this.markerEngine.handleSubscribeResult(feed, result.feedMarker, result.bitmap);
    this.markRecentlySubscribed(feed.id);
    if (result.refreshing)
      this.markRefreshing(feed.id);
    this.broadcastReload();
    this.notifyFeedsChange();
    if (!feed.postCount)
      this.pollForNewFeedPosts(feed.id);
  }

  /** Poll for a newly subscribed feed's posts until they arrive */
  private async pollForNewFeedPosts(feedId: string) {
    const maxAttempts = 12; // ~60s total
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 5000));
      if (!this.auth || !this.data) return;
      // Stop if feed was unsubscribed in the meantime
      if (!this.data.feeds.find(f => f.id == feedId)) return;
      try {
        const updated = await getFeed(this.auth, feedId);
        this.markOnlineSuccess();
        const feed = this.data.feeds.find(f => f.id == feedId);
        if (!feed) return;
        const hadPosts = feed.postCount > 0;
        feed.postCount = updated.postCount;
        feed.lastNewPostAtSemi = updated.lastNewPostAtSemi;
        feed.feedStatus = updated.feedStatus;
        // The subscribe response was written before the feed had ever stored a post, so this is
        // where the record first learns where its current.dz is. Without it the post engine can
        // only try the CDN — which for a one-subscriber feed never has the file.
        applyCurrentFileLocation(feed, updated);
        if (updated.postCount) {
          // Posts arrived — update marker unread count and notify. They are readable right away
          // (the client falls back to the sync instance while the CDN copy is still on its way),
          // so this also ends the "refreshing" spinner.
          if (!hadPosts)
            this.markerEngine.handleNewFeedPosts(feedId, updated.postCount);
          this.notifyFeedsChange();
          this.refreshingFeeds.delete(feedId);
          return;
        }
      } catch (e) {
        if (isNoResponseError(e)) this.setOfflineMode('poll');
        // ignore fetch errors, retry
      }
    }
  }

  async unsubscribe(feedId: string): Promise<boolean> {
    if (!this.auth || !this.data) return false;

    try {
      await unsubscribeFeed(this.auth, feedId);

      // Remove from local data
      this.data.feeds = this.data.feeds.filter(f => f.id != feedId);
      this.markerEngine.handleUnsubscribe(feedId);
      this.recentlySubscribed.delete(feedId);
      this.refreshingFeeds.delete(feedId);
      this.feedSemis.delete(feedId);
      const pendingEnforce = this.enforceDebounce.get(feedId);
      if (pendingEnforce) {
        clearTimeout(pendingEnforce);
        this.enforceDebounce.delete(feedId);
      }

      // Drop any custom display name so it doesn't linger in saved preferences.
      removeCustomFeedName(feedId);

      // Remove from all folders
      for (const [, feedIds] of Object.entries(this.data.folders)) {
        const idx = feedIds.indexOf(feedId);
        if (idx >= 0) feedIds.splice(idx, 1);
      }

      // Notify other tabs to reload
      this.broadcastReload();

      // Notify about feeds/folders change
      this.notifyFeedsChange();

      return true;
    } catch (e) {
      console.error('Unsubscribe failed:', e);
      return false;
    }
  }

  // --- Folder Management ---

  async setFeedFolder(feedId: string, folderName: string | null): Promise<boolean> {
    return this.moveFeed(feedId, folderName);
  }

  async moveFeed(feedId: string, folderName: string | null, position?: number): Promise<boolean> {
    if (!this.auth || !this.data) return false;

    // Use "" key for unfiled feed ordering
    const targetKey = folderName ?? '';

    // Copy each list while dropping the moved feed; splicing the live arrays would
    // leave engine and UI state corrupted if the server update fails.
    const newFolders: RestFolderUpdate = {};
    for (const [key, feedIds] of Object.entries(this.data.folders))
      newFolders[key] = feedIds.filter(id => id != feedId);
    if (!newFolders[targetKey]) newFolders[targetKey] = [];

    // The unfiled order may omit feeds that were never explicitly placed; they render
    // after the ordered ones. Materialize them so `position` matches the visible order.
    if (!targetKey) {
      const placed = new Set(Object.values(newFolders).flat());
      for (const feed of this.getFeeds())
        if (feed.id != feedId && !placed.has(feed.id)) newFolders[targetKey].push(feed.id);
    }

    if (position != null)
      newFolders[targetKey].splice(position, 0, feedId);
    else
      newFolders[targetKey].push(feedId);

    try {
      const result = await updateFolders(this.auth, newFolders);
      this.data.folders = result;

      this.broadcastReload();
      this.notifyFeedsChange();

      return true;
    } catch (e) {
      console.error('Update folders failed:', e);
      return false;
    }
  }

  /**
   * Move several feeds at once into `folderName` as a contiguous block, inserted at
   * `position` (an index into the target folder's list *after* the moved feeds have
   * been removed from wherever they were). Atomic: one folders update.
   */
  async moveFeeds(feedIds: string[], folderName: string | null, position?: number): Promise<boolean> {
    if (!this.auth || !this.data || !feedIds.length) return false;

    const targetKey = folderName ?? '';
    const moving = new Set(feedIds);
    // Copy each list so we don't mutate the engine's current state in place.
    const newFolders: RestFolderUpdate = {};
    for (const [key, ids] of Object.entries(this.data.folders))
      newFolders[key] = ids.filter(id => !moving.has(id));
    if (!newFolders[targetKey]) newFolders[targetKey] = [];

    const at = position != null ? Math.min(Math.max(position, 0), newFolders[targetKey].length) : newFolders[targetKey].length;
    newFolders[targetKey].splice(at, 0, ...feedIds);

    // Apply optimistically so the UI stays exactly where the user dropped, then persist.
    this.data.folders = newFolders;
    this.notifyFeedsChange();

    try {
      const result = await updateFolders(this.auth, newFolders);
      this.data.folders = result;
      this.broadcastReload();
      this.notifyFeedsChange();
      return true;
    } catch (e) {
      console.error('Update folders failed:', e);
      return false;
    }
  }

  async addEmptyFolder(folderName: string): Promise<boolean> {
    if (!this.auth || !this.data) return false;

    // Check if folder already exists
    if (this.data.folders[folderName]) {
      console.warn('Folder already exists:', folderName);
      return false;
    }

    // Create new folders object with empty folder
    const newFolders: RestFolderUpdate = {
      ...this.data.folders,
      [folderName]: []
    };

    try {
      const result = await updateFolders(this.auth, newFolders);
      this.data.folders = result;

      // Notify other tabs to reload
      this.broadcastReload();

      // Notify about feeds/folders change
      this.notifyFeedsChange();

      return true;
    } catch (e) {
      console.error('Add empty folder failed:', e);
      return false;
    }
  }

  async deleteFolder(folderName: string, deleteFeeds: boolean): Promise<boolean> {
    if (!this.auth || !this.data) return false;

    const feedIds = this.data.folders[folderName];
    if (!feedIds) {
      console.warn('Folder does not exist:', folderName);
      return false;
    }

    // If deleting feeds, unsubscribe from each one
    if (deleteFeeds && feedIds.length > 0) {
      for (const feedId of [...feedIds]) {
        await this.unsubscribe(feedId);
      }
    }

    // Create new folders object without this folder
    const newFolders: RestFolderUpdate = { ...this.data.folders };

    // If keeping feeds, move them to unfiled ("")
    if (!deleteFeeds && feedIds.length > 0) {
      if (!newFolders['']) {
        newFolders[''] = [];
      }
      // Prepend feeds to unfiled list
      newFolders[''] = [...feedIds, ...newFolders['']];
    }

    delete newFolders[folderName];

    try {
      const result = await updateFolders(this.auth, newFolders);
      this.data.folders = result;

      // Notify other tabs to reload
      this.broadcastReload();

      // Notify about feeds/folders change
      this.notifyFeedsChange();

      return true;
    } catch (e) {
      console.error('Delete folder failed:', e);
      return false;
    }
  }

  async renameFolder(oldName: string, newName: string): Promise<boolean> {
    if (!this.auth || !this.data) return false;

    const trimmedNew = newName.trim();
    if (!trimmedNew || trimmedNew === oldName) {
      return false;
    }

    // Check if old folder exists
    if (!(oldName in this.data.folders)) {
      console.warn('Folder does not exist:', oldName);
      return false;
    }

    // Check if new name already exists
    if (trimmedNew in this.data.folders) {
      console.warn('Folder already exists:', trimmedNew);
      return false;
    }

    // Create new folders object with renamed folder
    const newFolders: RestFolderUpdate = {};
    for (const [key, value] of Object.entries(this.data.folders)) {
      if (key === oldName) {
        newFolders[trimmedNew] = value;
      } else {
        newFolders[key] = value;
      }
    }

    try {
      const result = await updateFolders(this.auth, newFolders);
      this.data.folders = result;

      // Notify other tabs to reload
      this.broadcastReload();

      // Notify about feeds/folders change
      this.notifyFeedsChange();

      return true;
    } catch (e) {
      console.error('Rename folder failed:', e);
      return false;
    }
  }

  /**
   * Reorder named folders. `orderedNames` lists the named folders in the desired
   * order; the unfiled bucket ("") is kept first and any folder missing from the
   * list is appended (safety). Folder key order is preserved through JSON storage.
   */
  async setFolderOrder(orderedNames: string[]): Promise<boolean> {
    if (!this.auth || !this.data) return false;

    const current = this.data.folders;
    const newFolders: RestFolderUpdate = {};
    if (current[''] !== undefined) newFolders[''] = current[''];
    for (const name of orderedNames)
      if (name != '' && current[name] !== undefined) newFolders[name] = current[name];
    for (const [key, value] of Object.entries(current))
      if (!(key in newFolders)) newFolders[key] = value;

    try {
      const result = await updateFolders(this.auth, newFolders);
      this.data.folders = result;
      this.broadcastReload();
      this.notifyFeedsChange();
      return true;
    } catch (e) {
      console.error('Reorder folders failed:', e);
      return false;
    }
  }

  /** Merge `source` folder's feeds into `target` (appended), then remove `source`. */
  async mergeFolders(source: string, target: string): Promise<boolean> {
    if (!this.auth || !this.data || source == target) return false;

    const src = this.data.folders[source];
    const tgt = this.data.folders[target];
    if (!src || !tgt) return false;

    const newFolders: RestFolderUpdate = { ...this.data.folders };
    newFolders[target] = [...tgt, ...src];
    delete newFolders[source];

    try {
      const result = await updateFolders(this.auth, newFolders);
      this.data.folders = result;
      this.broadcastReload();
      this.notifyFeedsChange();
      return true;
    } catch (e) {
      console.error('Merge folders failed:', e);
      return false;
    }
  }

  // --- Starred / Read Later ---

  isStarred(feedId: string, postIndex: number): boolean {
    return this.parsedStarred.get(feedId)?.has(postIndex) ?? false;
  }

  isReadLater(feedId: string, postIndex: number): boolean {
    return this.parsedReadLater.get(feedId)?.has(postIndex) ?? false;
  }

  getStarredForFeed(feedId: string): Set<number> {
    return this.parsedStarred.get(feedId) ?? new Set();
  }

  getReadLaterForFeed(feedId: string): Set<number> {
    return this.parsedReadLater.get(feedId) ?? new Set();
  }

  getStarredEntries(): string[] {
    return this.data?.starred ?? [];
  }

  getReadLaterEntries(): string[] {
    return this.data?.readLater ?? [];
  }

  private countStarMap(map: Map<string, Set<number>>): number {
    let count = 0;
    map.forEach(set => count += set.size);
    return count;
  }

  getStarredCount(): number {
    return this.countStarMap(this.parsedStarred);
  }

  getReadLaterCount(): number {
    return this.countStarMap(this.parsedReadLater);
  }

  private notifyStarChange() {
    const starred = this.getStarredCount();
    const readLater = this.getReadLaterCount();
    this.starChangeCallbacks.forEach(cb => cb(starred, readLater));
  }

  async fetchStarredList(count?: number, sortOrder?: 'newestFirst' | 'oldestFirst'): Promise<RestStarList | null> {
    return this.fetchStarListCached('starred', count, sortOrder);
  }

  async fetchReadLaterList(count?: number, sortOrder?: 'newestFirst' | 'oldestFirst'): Promise<RestStarList | null> {
    return this.fetchStarListCached('readlater', count, sortOrder);
  }

  private allStarListFeedsKnown(entries: string[]): boolean {
    for (const entry of entries) {
      const dashIdx = entry.lastIndexOf('-');
      if (dashIdx < 0) continue;
      const feedId = entry.substring(0, dashIdx);
      if (!this.getFeed(feedId)) return false;
    }
    return true;
  }

  private synthesizeStarList(listId: 'starred' | 'readlater', count?: number, sortOrder?: 'newestFirst' | 'oldestFirst'): RestStarList | null {
    if (!this.data) return null;
    const isStarred = listId == 'starred';
    const source = isStarred ? this.data.starred : this.data.readLater;
    const lastUpdate = isStarred ? this.data.starredUpdate : this.data.readLaterUpdate;

    const order = sortOrder ?? 'newestFirst';
    const ordered = order == 'newestFirst' ? [...source].reverse() : [...source];
    const cap = Math.min(count ?? 1000, 1000);
    const entries = ordered.slice(0, cap);

    const feeds: Record<string, RestUserFeed> = {};
    for (const entry of entries) {
      const dashIdx = entry.lastIndexOf('-');
      if (dashIdx < 0) continue;
      const feedId = entry.substring(0, dashIdx);
      const feed = this.getFeed(feedId);
      if (feed) feeds[feedId] = feed;
    }

    return {
      entries,
      totalCount: Math.min(source.length, 1000),
      lastUpdate,
      feeds
    };
  }

  private async fetchStarListCached(listId: 'starred' | 'readlater', count?: number, sortOrder?: 'newestFirst' | 'oldestFirst'): Promise<RestStarList | null> {
    if (!this.auth) return null;
    // Keyed by list id only: callers don't consume the resolved entry order
    // (they build lists from polled data and rely on the metadata side effects),
    // so concurrent fetches with different count/sort share one request.
    const inFlight = this.starListFetches.get(listId);
    if (inFlight) return inFlight;
    const promise: Promise<RestStarList | null> = this.fetchStarListUncached(listId, count, sortOrder)
      .finally(() => { if (this.starListFetches.get(listId) == promise) this.starListFetches.delete(listId); });
    this.starListFetches.set(listId, promise);
    return promise;
  }

  private async fetchStarListUncached(listId: 'starred' | 'readlater', count?: number, sortOrder?: 'newestFirst' | 'oldestFirst'): Promise<RestStarList | null> {
    if (!this.auth) return null;

    const isStarred = listId == 'starred';
    const cache = isStarred ? this.starredListCache : this.readLaterListCache;
    const cacheTime = isStarred ? this.starredListCacheTime : this.readLaterListCacheTime;
    const lastUpdate = isStarred ? this.starredListLastUpdate : this.readLaterListLastUpdate;

    // Return cached if fresh (< 60s)
    if (cache && Date.now() - cacheTime < 60000)
      return cache;

    // Fast path: entries arrive via the poll endpoint in data.starred/readLater.
    // This endpoint is only needed for metadata of feeds the user has unsubscribed
    // from. If every referenced feed's metadata is already known locally, skip
    // the network call and build the list from polled state.
    const localEntries = isStarred ? this.data?.starred : this.data?.readLater;
    if (localEntries && this.allStarListFeedsKnown(localEntries)) {
      const synthesized = this.synthesizeStarList(listId, count, sortOrder);
      if (synthesized) {
        if (isStarred) {
          this.starredListCache = synthesized;
          this.starredListCacheTime = Date.now();
          this.starredListLastUpdate = synthesized.lastUpdate;
        } else {
          this.readLaterListCache = synthesized;
          this.readLaterListCacheTime = Date.now();
          this.readLaterListLastUpdate = synthesized.lastUpdate;
        }
        return synthesized;
      }
    }

    // Fetch with ifModifiedSince if we have a previous lastUpdate
    const result = await getStarList(this.auth, listId, count, sortOrder, lastUpdate ?? undefined);

    if (result) {
      // Got new data — register feed metadata for non-subscribed feeds
      for (const [id, feed] of Object.entries(result.feeds))
        this.starListFeeds.set(id, feed);
      if (isStarred) {
        this.starredListCache = result;
        this.starredListCacheTime = Date.now();
        this.starredListLastUpdate = result.lastUpdate;
      } else {
        this.readLaterListCache = result;
        this.readLaterListCacheTime = Date.now();
        this.readLaterListLastUpdate = result.lastUpdate;
      }
      return result;
    }

    // null means not modified — return cached data (refresh cache time)
    if (cache) {
      if (isStarred) this.starredListCacheTime = Date.now();
      else this.readLaterListCacheTime = Date.now();
      return cache;
    }
    return null;
  }

  // --- Unread Feed Cache ---

  onUnreadFeedChange(callback: () => void): () => void {
    this.unreadFeedCallbacks.add(callback);
    return () => this.unreadFeedCallbacks.delete(callback);
  }

  /**
   * Checks unread feed entries against known feeds/postCounts.
   * Returns 'ok' if all valid, 'poll' if some post indices exceed known postCount,
   * or 'reload' if any feedId is completely unknown.
   */
  private validateUnreadFeedEntries(entries: string[]): 'ok' | 'poll' | 'reload' {
    let needsPoll = false;
    for (const entry of entries) {
      const dashIdx = entry.lastIndexOf('-');
      if (dashIdx < 0) continue;
      const feedId = entry.substring(0, dashIdx);
      const postIndex = parseInt(entry.substring(dashIdx + 1), 10);

      const feed = this.getFeed(feedId);
      if (!feed) return 'reload'; // unknown feed — need full reload
      if (postIndex >= feed.postCount) needsPoll = true; // index beyond known postCount
    }
    return needsPoll ? 'poll' : 'ok';
  }

  /** Removes entries whose feedId is unknown or postIndex >= postCount */
  private filterInvalidUnreadEntries(entries: string[]): string[] {
    return entries.filter(entry => {
      const dashIdx = entry.lastIndexOf('-');
      if (dashIdx < 0) return false;
      const feedId = entry.substring(0, dashIdx);
      const postIndex = parseInt(entry.substring(dashIdx + 1), 10);
      const feed = this.getFeed(feedId);
      return feed && postIndex < feed.postCount;
    });
  }

  private unreadCacheKey(sortOrder: 'newestFirst' | 'oldestFirst', folder?: string): string {
    return folder == undefined ? sortOrder : `${sortOrder}|${folder}`;
  }

  async fetchUnreadFeed(sortOrder: 'newestFirst' | 'oldestFirst', count = 1000, folder?: string): Promise<RestUnreadFeed | null> {
    if (!this.auth) return null;

    const cache = this.unreadFeedCaches.get(this.unreadCacheKey(sortOrder, folder));

    // Cache exists and not stale — return cached
    if (cache && !cache.newPostsSinceLoad)
      return { entries: cache.entries, serverTime: cache.serverTime, cursor: cache.cursor, feeds: cache.feeds, oldestSemi: cache.oldestSemi, newestSemi: cache.newestSemi };

    // Cache exists but stale — check with ifModifiedSince
    if (cache) {
      const result = await getUnreadFeed(this.auth, sortOrder, count, undefined, cache.serverTime.toString(), folder);
      if (!result) {
        // 304 — not modified
        cache.newPostsSinceLoad = false;
        return { entries: cache.entries, serverTime: cache.serverTime, cursor: cache.cursor, feeds: cache.feeds, oldestSemi: cache.oldestSemi, newestSemi: cache.newestSemi };
      }
      this.storeUnreadFeedCache(sortOrder, result, folder);
      return this.validateAndFixUnreadFeed(sortOrder, result, folder);
    }

    // No cache — fresh load
    const result = await getUnreadFeed(this.auth, sortOrder, count, undefined, undefined, folder);
    if (!result) return null;
    this.storeUnreadFeedCache(sortOrder, result, folder);
    return this.validateAndFixUnreadFeed(sortOrder, result, folder);
  }

  /** After fetching unread feed, validate entries and poll/reload if needed */
  private async validateAndFixUnreadFeed(sortOrder: 'newestFirst' | 'oldestFirst', result: RestUnreadFeed, folder?: string): Promise<RestUnreadFeed> {
    const status = this.validateUnreadFeedEntries(result.entries);
    if (status == 'ok') return result;

    // Try to fix by polling or reloading
    try {
      if (status == 'reload')
        await this.reloadData();
      else
        await this.poll();
    } catch (e) {
      console.error('Failed to refresh feeds for unread validation:', e);
    }

    // Filter out any entries that are still invalid after poll/reload
    const filtered = this.filterInvalidUnreadEntries(result.entries);
    if (filtered.length != result.entries.length) {
      console.warn(`Filtered ${result.entries.length - filtered.length} invalid unread entries`);
      result = { ...result, entries: filtered };
      // Update the cache with filtered entries
      this.storeUnreadFeedCache(sortOrder, result, folder);
    }
    return result;
  }

  private storeUnreadFeedCache(sortOrder: 'newestFirst' | 'oldestFirst', result: RestUnreadFeed, folder?: string) {
    const cache: UnreadFeedCache = {
      entries: [...result.entries],
      feeds: { ...result.feeds },
      serverTime: result.serverTime,
      cursor: result.cursor,
      oldestSemi: result.oldestSemi,
      newestSemi: result.newestSemi,
      loadTime: Date.now(),
      newPostsSinceLoad: false
    };
    this.unreadFeedCaches.set(this.unreadCacheKey(sortOrder, folder), cache);

    // Register feed metadata so getFeed() works for cross-feed rendering
    for (const [id, feed] of Object.entries(result.feeds))
      this.starListFeeds.set(id, feed);
  }

  getUnreadFeedEntries(sortOrder: 'newestFirst' | 'oldestFirst', folder?: string): string[] | null {
    return this.unreadFeedCaches.get(this.unreadCacheKey(sortOrder, folder))?.entries ?? null;
  }

  getUnreadFeedInfo(sortOrder: 'newestFirst' | 'oldestFirst', folder?: string): { hasMore: boolean; totalLoaded: number } | null {
    const cache = this.unreadFeedCaches.get(this.unreadCacheKey(sortOrder, folder));
    if (!cache) return null;
    return { hasMore: !!cache.cursor, totalLoaded: cache.entries.length };
  }

  clearUnreadFeedCache(sortOrder?: 'newestFirst' | 'oldestFirst', folder?: string) {
    if (!sortOrder) this.unreadFeedCaches.clear();
    else this.unreadFeedCaches.delete(this.unreadCacheKey(sortOrder, folder));
  }

  starPost(feedId: string, postIndex: number): boolean {
    // Refuse new saves at the cap (matches the server's limit, which would otherwise silently
    // drop the oldest entry). Re-adds of existing entries always pass.
    if (!this.isStarred(feedId, postIndex) && this.getStarredCount() >= MAX_SAVED_POSTS)
      return false;
    this.updateLocalStarSet('starred', this.parsedStarred, feedId, postIndex, true);
    this.queueStarChange('starred', { added: [`${feedId}/${postIndex}`] });
    this.updateStarListCache('starred', feedId, postIndex, true);
    return true;
  }

  unstarPost(feedId: string, postIndex: number): void {
    this.updateLocalStarSet('starred', this.parsedStarred, feedId, postIndex, false);
    this.queueStarChange('starred', { removed: [`${feedId}/${postIndex}`] });
    this.updateStarListCache('starred', feedId, postIndex, false);
  }

  markReadLater(feedId: string, postIndex: number): boolean {
    if (!this.isReadLater(feedId, postIndex) && this.getReadLaterCount() >= MAX_SAVED_POSTS)
      return false;
    this.updateLocalStarSet('readLater', this.parsedReadLater, feedId, postIndex, true);
    this.queueStarChange('readLater', { added: [`${feedId}/${postIndex}`] });
    this.updateStarListCache('readlater', feedId, postIndex, true);
    return true;
  }

  unmarkReadLater(feedId: string, postIndex: number): void {
    this.updateLocalStarSet('readLater', this.parsedReadLater, feedId, postIndex, false);
    this.queueStarChange('readLater', { removed: [`${feedId}/${postIndex}`] });
    this.updateStarListCache('readlater', feedId, postIndex, false);
  }

  private updateStarListCache(listId: 'starred' | 'readlater', feedId: string, postIndex: number, add: boolean) {
    const cache = listId == 'starred' ? this.starredListCache : this.readLaterListCache;
    if (!cache) return;

    const entry = `${feedId}-${postIndex}`;
    if (add) {
      cache.entries = cache.entries.filter(e => e != entry);
      cache.entries.push(entry);
      if (cache.totalCount < MAX_SAVED_POSTS) cache.totalCount++;
    } else {
      const before = cache.entries.length;
      cache.entries = cache.entries.filter(e => e != entry);
      if (cache.entries.length < before)
        cache.totalCount = Math.max(0, cache.totalCount - 1);
    }

    const now = new Date().toISOString();
    cache.lastUpdate = now;
    if (listId == 'starred') this.starredListLastUpdate = now;
    else this.readLaterListLastUpdate = now;
  }

  private updateLocalStarSet(field: 'starred' | 'readLater', map: Map<string, Set<number>>, feedId: string, postIndex: number, add: boolean) {
    if (add) {
      let set = map.get(feedId);
      if (!set) {
        set = new Set();
        map.set(feedId, set);
      }
      set.add(postIndex);
    } else {
      const set = map.get(feedId);
      if (set) {
        set.delete(postIndex);
        if (!set.size) map.delete(feedId);
      }
    }

    if (this.data) {
      const key = `${feedId}-${postIndex}`;
      const entries = field == 'starred' ? this.data.starred : this.data.readLater;
      if (add) {
        if (!entries.includes(key)) entries.push(key);
      } else {
        const idx = entries.indexOf(key);
        if (idx >= 0) entries.splice(idx, 1);
      }
      const now = new Date().toISOString();
      if (field == 'starred') this.data.starredUpdate = now;
      else this.data.readLaterUpdate = now;
    }

    this.broadcastStarData();
    this.notifyStarChange();
  }

  // Debounce by SYNC_DELAY_MS, but never defer past MAX_STAR_SYNC_DELAY_MS from
  // the first un-flushed change, so a long marking session can't sit unsynced
  // (and get clobbered by a poll's marker update — see applyMarkerUpdate).
  private starSyncDelay(firstQueuedAt: number): number {
    return Math.max(0, Math.min(SYNC_DELAY_MS, firstQueuedAt + MAX_STAR_SYNC_DELAY_MS - Date.now()));
  }

  private queueStarChange(field: 'starred' | 'readLater', patch: PostStarPatch) {
    const now = Date.now();
    if (field == 'starred') {
      this.pendingStarredPatch = this.pendingStarredPatch ? mergePostStarPatches(this.pendingStarredPatch, patch) : patch;
      if (!this.starredFirstQueuedAt) this.starredFirstQueuedAt = now;
      if (this.starredSyncTimer) clearTimeout(this.starredSyncTimer);
      this.starredSyncTimer = setTimeout(() => this.flushStarredSync(), this.starSyncDelay(this.starredFirstQueuedAt));
    } else {
      this.pendingReadLaterPatch = this.pendingReadLaterPatch ? mergePostStarPatches(this.pendingReadLaterPatch, patch) : patch;
      if (!this.readLaterFirstQueuedAt) this.readLaterFirstQueuedAt = now;
      if (this.readLaterSyncTimer) clearTimeout(this.readLaterSyncTimer);
      this.readLaterSyncTimer = setTimeout(() => this.flushReadLaterSync(), this.starSyncDelay(this.readLaterFirstQueuedAt));
    }
  }

  private async flushStarredSync(): Promise<void> {
    if (this.starredSyncTimer) { clearTimeout(this.starredSyncTimer); this.starredSyncTimer = null; }
    this.starredFirstQueuedAt = 0;
    if (!this.auth || !this.pendingStarredPatch) return;
    const patch = this.pendingStarredPatch;
    this.pendingStarredPatch = null;
    try {
      await patchStarred(this.auth, patch);
    } catch (e) {
      console.error('Failed to sync starred:', e);
      this.pendingStarredPatch = this.pendingStarredPatch ? mergePostStarPatches(patch, this.pendingStarredPatch) : patch;
      // Retry every minute until it lands. Guarded on auth so a failure during
      // disable()/logout doesn't leave an orphan retry timer.
      if (this.auth) {
        if (this.starredSyncTimer) clearTimeout(this.starredSyncTimer);
        this.starredSyncTimer = setTimeout(() => this.flushStarredSync(), STAR_SYNC_RETRY_MS);
      }
    }
  }

  private async flushReadLaterSync(): Promise<void> {
    if (this.readLaterSyncTimer) { clearTimeout(this.readLaterSyncTimer); this.readLaterSyncTimer = null; }
    this.readLaterFirstQueuedAt = 0;
    if (!this.auth || !this.pendingReadLaterPatch) return;
    const patch = this.pendingReadLaterPatch;
    this.pendingReadLaterPatch = null;
    try {
      await patchReadLater(this.auth, patch);
    } catch (e) {
      console.error('Failed to sync read-later:', e);
      this.pendingReadLaterPatch = this.pendingReadLaterPatch ? mergePostStarPatches(patch, this.pendingReadLaterPatch) : patch;
      // Retry every minute until it lands. Guarded on auth so a failure during
      // disable()/logout doesn't leave an orphan retry timer.
      if (this.auth) {
        if (this.readLaterSyncTimer) clearTimeout(this.readLaterSyncTimer);
        this.readLaterSyncTimer = setTimeout(() => this.flushReadLaterSync(), STAR_SYNC_RETRY_MS);
      }
    }
  }

  private parseStarData() {
    if (!this.data) return;
    const oldStarredCount = this.countStarMap(this.parsedStarred);
    const oldReadLaterCount = this.countStarMap(this.parsedReadLater);
    this.parsedStarred = this.parseStarArray(this.data.starred);
    this.parsedReadLater = this.parseStarArray(this.data.readLater);
    if (this.countStarMap(this.parsedStarred) != oldStarredCount || this.countStarMap(this.parsedReadLater) != oldReadLaterCount)
      this.notifyStarChange();
  }

  private parseStarArray(entries: string[] | undefined): Map<string, Set<number>> {
    const map = new Map<string, Set<number>>();
    if (!entries) return map;
    for (const entry of entries) {
      const dashIdx = entry.lastIndexOf('-');
      const feedId = entry.substring(0, dashIdx);
      const postIndex = parseInt(entry.substring(dashIdx + 1), 10);
      const set = map.get(feedId);
      if (set) set.add(postIndex);
      else map.set(feedId, new Set([postIndex]));
    }
    return map;
  }

  /**
   * Re-apply a not-yet-synced pending star/read-later patch on top of a server
   * entry array. Server arrays use "feedId-postIndex" (dash); patches use
   * "feedId/postIndex" (slash) and may contain ranges or removeAll. Server
   * order is preserved; locally-added entries are appended (so newest-first
   * ordering surfaces them first). Without this, a poll's marker update (or a
   * broadcast from another tab) would clobber star/read-later changes the user
   * just made but that haven't been POSTed to the server yet.
   */
  private reconcileStarEntries(serverEntries: string[], patch: PostStarPatch | null): string[] {
    if (!patch) return serverEntries;
    const present = new Set(patch.removeAll ? [] : serverEntries);
    for (const [feedId, idxs] of Array.from(parseStarEntries(patch.removed)))
      for (const idx of Array.from(idxs)) present.delete(`${feedId}-${idx}`);
    for (const [feedId, idxs] of Array.from(parseStarEntries(patch.added)))
      for (const idx of Array.from(idxs)) present.add(`${feedId}-${idx}`);
    return Array.from(present);
  }

  private handleStarMessage(msg: BroadcastStarMessage) {
    if (!this.data) return;
    const nowIso = new Date().toISOString();
    this.data.starred = this.reconcileStarEntries(msg.starred, this.pendingStarredPatch);
    this.data.starredUpdate = this.pendingStarredPatch ? nowIso : msg.starredUpdate;
    this.data.readLater = this.reconcileStarEntries(msg.readLater, this.pendingReadLaterPatch);
    this.data.readLaterUpdate = this.pendingReadLaterPatch ? nowIso : msg.readLaterUpdate;
    this.parseStarData();

    // Invalidate star list caches so next fetch gets fresh data
    this.starredListCache = null;
    this.starredListCacheTime = 0;
    this.readLaterListCache = null;
    this.readLaterListCacheTime = 0;
  }

  private broadcastStarData() {
    if (!this.broadcastChannel || !this.auth || !this.data) return;
    const msg: BroadcastStarMessage = {
      type: 'star',
      authId: this.auth.id,
      starred: this.data.starred,
      starredUpdate: this.data.starredUpdate,
      readLater: this.data.readLater,
      readLaterUpdate: this.data.readLaterUpdate
    };
    console.log(`[broadcast] → sending 'star' (${msg.starred.length} starred, ${msg.readLater.length} read-later)`, msg);
    this.broadcastChannel.postMessage(msg);
  }
}

